import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trash2, LogOut, ArrowLeft, Lock, Mail, Building2, MessageSquare, Reply, Send, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "alkyrvis_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [repliedIds, setRepliedIds] = useState(new Set());
  const [digestStatus, setDigestStatus] = useState("");
  const [tab, setTab] = useState("enquiries");
  const [convos, setConvos] = useState([]);
  const [openConvo, setOpenConvo] = useState(null);
  const [convoMessages, setConvoMessages] = useState({});

  const load = useCallback(
    async (t) => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/admin/enquiries`, {
          headers: { Authorization: `Bearer ${t || token}` },
        });
        if (res.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
          return;
        }
        const j = await res.json();
        setEnquiries(j.enquiries || []);
      } catch {
        setError("Could not load enquiries.");
      }
      setLoading(false);
    },
    [token]
  );

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const loadConvos = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const j = await res.json();
        setConvos(j.conversations || []);
      }
    } catch {
      /* ignore */
    }
  }, [token]);

  useEffect(() => {
    if (token && tab === "conversations") loadConvos();
  }, [token, tab, loadConvos]);

  const toggleConvo = async (sid) => {
    if (openConvo === sid) {
      setOpenConvo(null);
      return;
    }
    setOpenConvo(sid);
    if (!convoMessages[sid]) {
      try {
        const res = await fetch(`${API}/admin/conversations/${sid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const j = await res.json();
          setConvoMessages((m) => ({ ...m, [sid]: j.messages || [] }));
        }
      } catch {
        /* ignore */
      }
    }
  };

  const login = async () => {
    if (!password.trim()) return;
    setError("");
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const j = await res.json();
      if (!res.ok) {
        setError(typeof j.detail === "string" ? j.detail : "Login failed.");
        return;
      }
      sessionStorage.setItem(TOKEN_KEY, j.token);
      setToken(j.token);
      setPassword("");
    } catch {
      setError("Login failed. Please try again.");
    }
  };

  const remove = async (id) => {
    try {
      await fetch(`${API}/admin/enquiries/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries((e) => e.filter((x) => x.id !== id));
    } catch {
      /* ignore */
    }
  };

  const sendReply = async (id) => {
    if (!replyText.trim() || replyBusy) return;
    setReplyBusy(true);
    try {
      const res = await fetch(`${API}/admin/enquiries/${id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: replyText }),
      });
      if (res.ok) {
        setRepliedIds((s) => new Set(s).add(id));
        setReplyFor(null);
        setReplyText("");
      }
    } catch {
      /* ignore */
    }
    setReplyBusy(false);
  };

  const sendDigest = async () => {
    setDigestStatus("sending");
    try {
      const res = await fetch(`${API}/admin/weekly-summary`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setDigestStatus(res.ok ? "sent" : "failed");
    } catch {
      setDigestStatus("failed");
    }
    setTimeout(() => setDigestStatus(""), 4000);
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries([]);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6 font-body text-[#F5F5F5]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="glass-panel w-full max-w-sm rounded-2xl border-gold/20 p-9"
          data-testid="admin-login-form"
        >
          <div className="flex flex-col items-center text-center">
            <img
              src="/assets/emblem.png"
              alt=""
              className="h-14 w-14 object-contain drop-shadow-[0_0_20px_rgba(216,192,138,0.6)]"
            />
            <h1 className="mt-5 font-display text-2xl font-light tracking-tight">
              Alkyrvis Command
            </h1>
            <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-gold/70">
              Private — Enquiries Dashboard
            </p>
          </div>
          <div className="mt-8 flex items-center gap-2.5 rounded-xl border border-white/10 bg-black/50 px-4 py-1.5 transition-[border-color] duration-300 focus-within:border-gold/50">
            <Lock className="h-4 w-4 shrink-0 text-gold" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && login()}
              placeholder="Admin password"
              data-testid="admin-password-input"
              className="w-full bg-transparent py-2.5 text-sm text-[#F5F5F5] outline-none placeholder:text-white/25"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mt-3 text-center font-mono text-[11px] text-red-400">
              {error}
            </p>
          )}
          <button
            type="button"
            data-testid="admin-login-button"
            onClick={login}
            className="mt-5 w-full rounded-full bg-gold py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#050505] transition-[box-shadow,background-color] duration-300 hover:bg-gold-bright hover:shadow-[0_0_40px_rgba(216,192,138,0.4)]"
          >
            Enter Dashboard
          </button>
          <a
            href="/"
            data-testid="admin-back-link"
            className="mt-5 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
          >
            <ArrowLeft className="h-3 w-3" /> Back to site
          </a>
        </motion.div>
      </div>
    );
  }

  const conciergeCount = enquiries.filter((e) => e.source === "concierge").length;

  return (
    <div className="min-h-screen bg-[#050505] font-body text-[#F5F5F5]" data-testid="admin-dashboard">
      <header className="border-b border-white/5 bg-[#070707]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <img src="/assets/emblem.png" alt="" className="h-9 w-9 object-contain drop-shadow-[0_0_12px_rgba(216,192,138,0.5)]" />
            <div>
              <p className="font-display text-sm font-medium uppercase tracking-[0.35em]">Alkyrvis</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold/70">Enquiries Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <button
              type="button"
              data-testid="admin-digest-button"
              onClick={sendDigest}
              className="flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold transition-[background-color] duration-300 hover:bg-gold/10"
            >
              <CalendarClock className="h-3 w-3" />
              {digestStatus === "sending" ? "Sending..." : digestStatus === "sent" ? "Digest Sent" : digestStatus === "failed" ? "Failed" : "Weekly Digest"}
            </button>
            <a href="/" data-testid="admin-back-link" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold">
              <ArrowLeft className="h-3 w-3" /> Site
            </a>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-[border-color,color] duration-300 hover:border-gold/40 hover:text-gold"
            >
              <LogOut className="h-3 w-3" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Total Enquiries", value: enquiries.length, id: "total" },
            { label: "Via Concierge", value: conciergeCount, id: "concierge" },
            { label: "Via Form", value: enquiries.length - conciergeCount, id: "form" },
          ].map((s) => (
            <div key={s.id} className="glass-panel rounded-2xl p-6" data-testid={`admin-stat-${s.id}`}>
              <p className="font-display text-4xl font-light text-gold">{s.value}</p>
              <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3]">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex gap-2.5">
          {["enquiries", "conversations"].map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`rounded-full px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-[border-color,color,background-color] duration-300 ${
                tab === t
                  ? "border border-gold/50 bg-gold/10 text-gold"
                  : "border border-white/10 text-[#A3A3A3] hover:border-gold/30 hover:text-gold"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="mt-8 space-y-3">
            {loading && enquiries.length === 0 && (
              <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.3em] text-[#A3A3A3]">Loading...</p>
            )}
            {!loading && enquiries.length === 0 && (
              <p data-testid="admin-empty-state" className="py-16 text-center font-mono text-xs uppercase tracking-[0.3em] text-[#A3A3A3]">
                No enquiries yet
              </p>
            )}
            {enquiries.map((e) => (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="glass-panel card-glow-hover flex flex-col gap-4 rounded-2xl p-6 md:flex-row md:flex-wrap md:items-center md:justify-between"
                data-testid={`enquiry-row-${e.id}`}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-display text-lg font-medium tracking-tight">{e.name}</p>
                    <span
                      className={`rounded-full px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] ${
                        e.source === "concierge"
                          ? "border border-mint/30 bg-mint/10 text-mint"
                          : "border border-gold/30 bg-gold/10 text-gold"
                      }`}
                      data-testid={`enquiry-source-${e.id}`}
                    >
                      {e.source === "concierge" ? "Concierge" : "Form"}
                    </span>
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-[#A3A3A3]">
                    <span className="flex items-center gap-2">
                      <Mail className="h-3.5 w-3.5 text-gold/60" /> {e.email}
                    </span>
                    {e.company && (
                      <span className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 text-gold/60" /> {e.company}
                      </span>
                    )}
                    {e.message && (
                      <span className="flex items-center gap-2">
                        <MessageSquare className="h-3.5 w-3.5 text-gold/60" /> {e.message}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 font-mono text-[10px] tracking-wider text-white/25">
                    {e.timestamp ? new Date(e.timestamp).toLocaleString() : ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2.5 self-start md:self-center">
                  <button
                    type="button"
                    data-testid={`enquiry-reply-${e.id}`}
                    onClick={() => {
                      setReplyFor(replyFor === e.id ? null : e.id);
                      setReplyText("");
                    }}
                    className={`flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-[border-color,color] duration-300 ${
                      repliedIds.has(e.id) || (e.replies && e.replies.length > 0)
                        ? "border-mint/30 text-mint"
                        : "border-gold/30 text-gold hover:bg-gold/10"
                    }`}
                  >
                    <Reply className="h-3.5 w-3.5" />
                    {repliedIds.has(e.id) || (e.replies && e.replies.length > 0) ? "Replied" : "Reply"}
                  </button>
                  <button
                    type="button"
                    data-testid={`enquiry-delete-${e.id}`}
                    onClick={() => remove(e.id)}
                    className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-[border-color,color] duration-300 hover:border-red-400/40 hover:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
                {replyFor === e.id && (
                  <div className="w-full border-t border-white/5 pt-4" data-testid={`reply-composer-${e.id}`}>
                    <textarea
                      value={replyText}
                      onChange={(ev) => setReplyText(ev.target.value)}
                      placeholder={`Reply to ${e.name}...`}
                      rows={3}
                      data-testid={`reply-input-${e.id}`}
                      className="w-full resize-none rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm text-[#F5F5F5] outline-none transition-[border-color] duration-300 placeholder:text-white/25 focus:border-gold/50"
                    />
                    <div className="mt-3 flex gap-2.5">
                      <button
                        type="button"
                        data-testid={`reply-send-${e.id}`}
                        onClick={() => sendReply(e.id)}
                        disabled={replyBusy || !replyText.trim()}
                        className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-[#050505] transition-[background-color] duration-300 hover:bg-gold-bright disabled:opacity-40"
                      >
                        <Send className="h-3 w-3" /> {replyBusy ? "Sending..." : "Send Reply"}
                      </button>
                      <button
                        type="button"
                        data-testid={`reply-cancel-${e.id}`}
                        onClick={() => setReplyFor(null)}
                        className="rounded-full border border-white/10 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {tab === "conversations" && (
          <div className="mt-8 space-y-3" data-testid="admin-conversations">
            {convos.length === 0 && (
              <p data-testid="admin-convos-empty" className="py-16 text-center font-mono text-xs uppercase tracking-[0.3em] text-[#A3A3A3]">
                No conversations yet
              </p>
            )}
            {convos.map((c) => (
              <div
                key={c.session_id}
                className="glass-panel rounded-2xl p-6"
                data-testid={`convo-row-${c.session_id.slice(0, 8)}`}
              >
                <button
                  type="button"
                  className="w-full text-left"
                  data-testid={`convo-open-${c.session_id.slice(0, 8)}`}
                  onClick={() => toggleConvo(c.session_id)}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">
                      Session {c.session_id.slice(0, 8)}
                    </p>
                    <span className="rounded-full border border-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#A3A3A3]">
                      {c.messages} messages
                    </span>
                  </div>
                  <p className="mt-2.5 truncate text-sm text-[#A3A3A3]">
                    {c.last_role === "user" ? "Visitor: " : "Concierge: "}
                    {c.last_message}
                  </p>
                  <p className="mt-1.5 font-mono text-[10px] tracking-wider text-white/25">
                    {c.last_timestamp ? new Date(c.last_timestamp).toLocaleString() : ""}
                  </p>
                </button>
                {openConvo === c.session_id && convoMessages[c.session_id] && (
                  <div
                    className="mt-4 max-h-96 space-y-3 overflow-y-auto border-t border-white/5 pt-4"
                    data-testid={`convo-transcript-${c.session_id.slice(0, 8)}`}
                  >
                    {convoMessages[c.session_id].map((m, i) => (
                      <div
                        key={i}
                        className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                            m.role === "user"
                              ? "rounded-br-sm border border-gold/25 bg-gold/15 text-gold-bright"
                              : "rounded-bl-sm border border-white/8 bg-white/[0.04] text-[#E8E8E8]"
                          }`}
                        >
                          {m.content}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
